import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
} from '@angular/core';
import { NavMenuHeader, NavMenuProps, NavMenuSection, OrganizationMenu } from '@bl/components';
import { type SnippetService } from '@bl/components/dist/types';
import { SvelteService } from '@bl/components/dist/wrappers';
import { BlFrontendBaseComponent } from './bl-frontend-base.component';
import { SnippetDirective } from './snippet-directives/snippet.directive';
import { SnippetDirectiveType } from './snippet-directives/types';

const NAV_MENU_EXPANDED_KEY = 'navMenuExpanded';
const getNavMenuExpandedStateFromStorage = (): boolean => {
  const storedValue = localStorage.getItem(NAV_MENU_EXPANDED_KEY);
  return storedValue === 'false' ? false : true; // Default to true if not set
};

@Component({
  selector: 'bl-frontend-nav-menu',
  template: '<ng-content select="[snippet]"></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [],
})
export class BlFrontendNavMenuComponent extends BlFrontendBaseComponent<NavMenuProps> {
  @ContentChild(SnippetDirective) snippet?: SnippetDirectiveType;

  @Input() menuHeader: NavMenuHeader;
  @Input() menuSections: NavMenuSection[];
  @Input() organizationMenu: OrganizationMenu;
  @Input() mobileNavMenuState: 'open' | 'closed';
  @Input() onClose: () => void;

  @Output() closeMenu = new EventEmitter<void>();
  @Output() menuToggled = new EventEmitter<boolean>();

  private isMenuExpanded = signal(getNavMenuExpandedStateFromStorage());

  constructor(ref: ElementRef) {
    super(ref);

    effect(() => {
      const isExpanded = this.isMenuExpanded();
      if (!this.snippet || !isExpanded) {
        this.clearMenuFooter();
        return;
      }
      this.updateMenuSnippet();
    });
  }

  protected override getComponentName(): string {
    return 'NavMenu';
  }

  protected override getProps(): NavMenuProps {
    return {
      menuSections: this.menuSections || [],
      // eslint-disable-next-line no-undefined
      organizationMenu: this.organizationMenu || undefined,
      // eslint-disable-next-line no-undefined
      menuHeader: this.menuHeader || undefined,
      mobileNavMenuState: this.mobileNavMenuState,
      onClose: () => {
        this.closeMenu.emit();
      },
      onMenuToggled: (isExpanded: boolean) => {
        this.isMenuExpanded.set(isExpanded);
        this.menuToggled.emit(isExpanded);
      },
    };
  }

  private updateMenuSnippet() {
    let propsToUpdate = {};

    const menuFooter = this.getMenuFooterSnippet();
    if (menuFooter) {
      propsToUpdate = { ...propsToUpdate, menuFooter: this.createSvelteSnippet(menuFooter.getHtmlContent()) };
      menuFooter.hideElement();
    }

    this.updateProps(propsToUpdate);
  }

  private snippetService = this.getService<SnippetService>(SvelteService.SnippetService);

  private createSvelteSnippet(template: string) {
    return this.snippetService.createSvelteSnippet(template);
  }

  private getMenuFooterSnippet(): SnippetDirectiveType | undefined {
    return this.snippet?.getSnippetSelector() === 'menuFooter' ? this.snippet : null;
  }

  private clearMenuFooter() {
    this.updateProps({ menuFooter: null });
    this.getMenuFooterSnippet()?.hideElement();
  }
}
